/*
===============================================================================================================
                                 Copyright 2023-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';
// eslint-disable-next-line import/no-cycle
import InlineEditableText from '@/components/inline-editable-text/inline-editable-text';
import TooltipIcon from '@/public/icons/tooltip.svg';
import Tooltip from '../tooltip/tooltip';

/**
 * Component that mimics the look and feel of a tooltip component but uses inline Editable text components
 * so that the user is able to edi tht values.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {func} onChange - Called when the save button is clicked
 * @param {string} defaultTooltip - The text content of the tooltip
 * @param {boolean} readOnly - Whether or not this is a read-only field
 * @param {boolean} useContainerWidth - Whether or not this should use width of its container
 * @param {string} defaultResetValue - A text string to display instead of an empty tooltip when resetting the value
 * @param {Object} onChangeValidationRules - Full description in error-reporting.jsx
 * @param {Object} onSubmitValidationRules - Full description in error-reporting.jsx
 */
export default function EditableTooltip({
  inputSelectorId,
  onChange,
  defaultTooltip,
  defaultResetValue,
  onChangeValidationRules,
  onSubmitValidationRules,
  maxLength,
  tooLongError,
  useContainerWidth,
  readOnly,
  description,
}) {
  const { translate, textDirection } = useContext(LocaleContext);
  const isRtl = textDirection === 'rtl';

  return (
    <>
      <div className="editable-text-tooltip-wrapper" id={inputSelectorId}>
        <Tooltip
          content={(
            <div className="tooltip-padded-content">
              {description && <div>{description}</div>}
              <InlineEditableText
                inputSelectorId={inputSelectorId}
                onChange={onChange}
                fontSize="var(--font-size-H2)"
                fontWeight="var(--font-weight-body)"
                defaultValue={defaultTooltip}
                defaultResetValue={defaultResetValue}
                readOnly={readOnly}
                useContainerWidth={useContainerWidth}
                textAreaMode
                maxLength={maxLength}
                validationRules={{
                  onChange: onChangeValidationRules,
                  onSubmit: onSubmitValidationRules,
                  tooLongError: translate(tooLongError),
                }}
              />
            </div>
          )}
          maxWidth={340}
          fullWidthMobile
          triggerOnClick
          disableMouseOver={readOnly}
          focusSelectorId={`${inputSelectorId}-edit-button`}
        >
          <div
            className="tooltip-button-wrapper"
            aria-label={translate('constants.tooltipLabels.questionMark')}
            role="button"
            tabIndex={0}
          >
            <TooltipIcon />
          </div>
        </Tooltip>
      </div>
      <style jsx>
        {`

          .editable-text-tooltip-wrapper {
            display: flex;
            align-items: center;
            width: 45px;
          }

          .tooltip-button-wrapper {
            cursor: pointer;
            display: flex;
            align-items: center;
            transform: translateY(-2px);
          }

          .tooltip-padded-content {
            padding: ${isRtl ? '0 0 0 10px' : '0 10px 0 0'};
          }
        `}
      </style>
    </>
  );
}

EditableTooltip.defaultProps = {
  inputSelectorId: null,
  defaultTooltip: null,
  defaultResetValue: null,
  onChange: () => {},
  onSubmitValidationRules: [],
  onChangeValidationRules: [],
  maxLength: null,
  tooLongError: null,
  readOnly: false,
  useContainerWidth: false,
  description: null,
};

EditableTooltip.propTypes = {
  inputSelectorId: PropTypes.string,
  defaultTooltip: PropTypes.string,
  defaultResetValue: PropTypes.string,
  onChange: PropTypes.func,
  onSubmitValidationRules: PropTypes.arrayOf(PropTypes.string),
  onChangeValidationRules: PropTypes.arrayOf(PropTypes.string),
  maxLength: PropTypes.number,
  tooLongError: PropTypes.string,
  readOnly: PropTypes.bool,
  useContainerWidth: PropTypes.bool,
  description: PropTypes.string,
};
